// Client-side mirror of the backend pricing engine (settings_store.compute_product_price).
// The backend remains the source of truth at checkout; this is only for live display.

export function kitPriceCents(settings, qty) {
  const kits = settings?.kits || [];
  for (const k of kits) {
    if (Number(k.qty) === Number(qty)) return Number(k.price_cents);
  }
  return null;
}

function findTier(settings, qty) {
  for (const tr of settings?.pricing_tiers || []) {
    const lo = Number(tr.min_qty);
    const hi = tr.max_qty === null || tr.max_qty === undefined || tr.max_qty === "" ? null : Number(tr.max_qty);
    if (qty >= lo && (hi === null || qty <= hi)) return tr;
  }
  return null;
}

// Returns { product_price_cents, pricing_source } or null if not purchasable.
export function computeProductPrice(settings, qty) {
  qty = Number(qty);
  if (!Number.isFinite(qty) || qty <= 0) return null;

  const kitPrice = kitPriceCents(settings, qty);
  if (kitPrice !== null) {
    return { product_price_cents: kitPrice, pricing_source: "featured_kit", unit_price_cents: null, quantity: qty };
  }

  const cq = settings?.custom_quantity || {};
  if (!cq.enabled) return null;
  const min = Number(cq.min_qty || 1);
  const max = cq.max_qty === null || cq.max_qty === undefined || cq.max_qty === "" ? null : Number(cq.max_qty);
  if (qty < min || (max !== null && qty > max)) return null;

  const tier = findTier(settings, qty);
  if (!tier) return null;
  const ppm = Number(tier.price_per_magnet_cents);
  return { product_price_cents: qty * ppm, pricing_source: "custom_tier", unit_price_cents: ppm, quantity: qty };
}

export function customRange(settings) {
  const cq = settings?.custom_quantity || {};
  const min = Number(cq.min_qty || 1);
  const max = cq.max_qty === null || cq.max_qty === undefined || cq.max_qty === "" ? null : Number(cq.max_qty);
  return { enabled: !!cq.enabled, min, max };
}

export const FIRST_ORDER_PCT = 10;

// 10% first-order discount on the MAGNET SUBTOTAL only. Mirrors the backend.
export function firstOrderDiscountCents(subtotalCents, active) {
  if (!active || !subtotalCents || subtotalCents <= 0) return 0;
  return Math.round(subtotalCents * FIRST_ORDER_PCT / 100);
}

// Active free-shipping state under the configured rule. `subtotalCents` must be
// the PRE-discount magnet subtotal. Returns null when no rule is configured.
export function freeShippingState(settings, qty, subtotalCents) {
  const sh = settings?.shipping || {};
  const rule = sh.free_rule === "magnet_qty" ? "magnet_qty" : "order_value";
  if (rule === "magnet_qty") {
    const thr = Number(sh.free_qty_threshold || 0);
    if (thr <= 0) return null;
    const q = Number(qty || 0);
    return { rule, threshold: thr, unlocked: q > 0 && q >= thr, remainingQty: Math.max(0, thr - q), hasValue: q > 0 };
  }
  const thr = Number(sh.free_threshold_cents || 0);
  if (thr <= 0) return null;
  const st = Number(subtotalCents || 0);
  return { rule, threshold: thr, unlocked: st > 0 && st >= thr, remainingCents: Math.max(0, thr - st), hasValue: st > 0 };
}

// Shipping cost for live display. Honors the active free-shipping rule.
export function shippingCents(settings, qty, subtotalCents) {
  const sh = settings?.shipping || {};
  const fs = freeShippingState(settings, qty, subtotalCents);
  if (fs && fs.unlocked) return 0;
  return Number(sh.placeholder_flat_cents ?? 600);
}

// Whether a featured kit qualifies for the automatic FREE SHIPPING badge.
export function kitQualifiesFreeShipping(settings, kit) {
  const sh = settings?.shipping || {};
  const rule = sh.free_rule === "magnet_qty" ? "magnet_qty" : "order_value";
  if (rule === "magnet_qty") {
    const thr = Number(sh.free_qty_threshold || 0);
    return thr > 0 && Number(kit?.qty || 0) >= thr;
  }
  const thr = Number(sh.free_threshold_cents || 0);
  return thr > 0 && Number(kit?.price_cents || 0) >= thr;
}
